import { createBrowserRouter, type RouteObject } from 'react-router';
import { Outlet } from 'react-router';
import { HomePage } from '../pages/HomePage';
import { AssetSearchPage } from '../pages/assets/AssetSearchPage';
import { MaintenanceSearchPage } from '../pages/assets/MaintenanceSearchPage';
import { AssetDetailPage, AssetDetailIndexRedirect } from '../pages/assets/AssetDetailPage';
import { AssetGeneralTab } from '../pages/assets/tabs/AssetGeneralTab';
import { AssetClockReadingsTab } from '../pages/assets/tabs/AssetClockReadingsTab';
import { AssetHistoryTab } from '../pages/assets/tabs/AssetHistoryTab';
import { AssetMaintenanceTab } from '../pages/assets/tabs/AssetMaintenanceTab';
import { AssetEquipmentLogTab } from '../pages/assets/tabs/AssetEquipmentLogTab';
import { AssetTabPlaceholder } from '../pages/assets/tabs/AssetTabPlaceholder';
import { PlantDetailIndexRedirect, PlantDetailPage } from '../pages/plants/PlantDetailPage';
import { PlantSearchPage } from '../pages/plants/PlantSearchPage';
import { PlantGeneralTab } from '../pages/plants/tabs/PlantGeneralTab';
import { PlantAssetsTab } from '../pages/plants/tabs/PlantAssetsTab';
import { ClockReadingSearchPage } from '../pages/clock-readings/ClockReadingSearchPage';
import { AssetMappingPage } from '../pages/asset-mapping/AssetMappingPage';
import { NoAccessPage } from '../pages/NoAccessPage';
import { AuthenticatedLayout } from './AuthenticatedLayout';
import type { BreadcrumbHandle } from './breadcrumbs';
import { administrationRoutes } from './administrationRoutes';

export const authenticatedRoutes: RouteObject[] = [
  {
    path: '/',
    Component: AuthenticatedLayout,
    handle: {
      breadcrumb: () => ({ label: 'Home', to: '/' }),
    } satisfies BreadcrumbHandle,
    children: [
      {
        index: true,
        Component: HomePage,
      },
      {
        path: 'assets',
        element: <Outlet />,
        handle: {
          breadcrumb: () => ({ label: 'Assets', to: '/assets' }),
        } satisfies BreadcrumbHandle,
        children: [
          {
            index: true,
            Component: AssetSearchPage,
          },
          {
            path: 'maintenance',
            Component: MaintenanceSearchPage,
            handle: {
              breadcrumb: () => ({ label: 'Maintenance Task Search', to: '/assets/maintenance' }),
            } satisfies BreadcrumbHandle,
          },
          {
            path: ':assetId',
            Component: AssetDetailPage,
            handle: {
              breadcrumb: ({ match, searchParams }) => ({
                label: searchParams.get('code') || `Asset ${match.params.assetId}`,
              }),
            } satisfies BreadcrumbHandle,
            children: [
              {
                index: true,
                Component: AssetDetailIndexRedirect,
              },
              {
                path: 'general',
                Component: AssetGeneralTab,
              },
              {
                path: 'images',
                element: <AssetTabPlaceholder tabName="Images" />,
              },
              {
                path: 'clock-readings',
                Component: AssetClockReadingsTab,
              },
              {
                path: 'planning',
                element: <AssetTabPlaceholder tabName="Planning Details" />,
              },
              {
                path: 'maintenance',
                Component: AssetMaintenanceTab,
              },
              {
                path: 'history',
                Component: AssetHistoryTab,
              },
              {
                path: 'equipment-log',
                Component: AssetEquipmentLogTab,
              },
              {
                path: 'documents',
                element: <AssetTabPlaceholder tabName="Documents" />,
              },
            ],
          },
        ],
      },
      {
        path: 'no-access',
        Component: NoAccessPage,
        handle: {
          breadcrumb: () => ({ label: 'Access Denied' }),
        } satisfies BreadcrumbHandle,
      },
      {
        path: 'plants',
        element: <Outlet />,
        handle: {
          breadcrumb: () => ({ label: 'Plants', to: '/plants' }),
        } satisfies BreadcrumbHandle,
        children: [
          {
            index: true,
            Component: PlantSearchPage,
          },
          {
            path: ':plantId',
            Component: PlantDetailPage,
            handle: {
              breadcrumb: ({ match, searchParams }) => ({
                label: searchParams.get('name') || `Plant ${match.params.plantId}`,
              }),
            } satisfies BreadcrumbHandle,
            children: [
              {
                index: true,
                Component: PlantDetailIndexRedirect,
              },
              {
                path: 'general',
                Component: PlantGeneralTab,
              },
              {
                path: 'assets',
                Component: PlantAssetsTab,
              },
            ],
          },
        ],
      },
      {
        path: 'clock-readings',
        Component: ClockReadingSearchPage,
        handle: {
          breadcrumb: () => ({ label: 'Clock Readings', to: '/clock-readings' }),
        } satisfies BreadcrumbHandle,
      },
      {
        path: 'asset-mapping',
        Component: AssetMappingPage,
        handle: {
          breadcrumb: () => ({ label: 'Asset Mapping', to: '/asset-mapping' }),
        } satisfies BreadcrumbHandle,
      },
      administrationRoutes,
    ],
  },
];

export const authenticatedRouter = createBrowserRouter(authenticatedRoutes);